import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, ChevronLeft, Upload, X, Paperclip, User } from "lucide-react";
import { toast } from "sonner";

const REQUIREMENTS = ["Kitchen", "Wardrobes", "Vanity", "Only Carcass", "Others"];
const TIMELINES = ["Immediate", "1 month", "2-3 months", "3+ months"];
const FIELD = "w-full bg-white border border-slate-300 rounded-sm p-2.5 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none";

export default function EnquiryNew() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [state, setState] = useState({
    name: "", phone: "", email: "", address: "",
    requirements: [], timeline: "", notes: "",
    assigned_sales_id: "", assigned_coordinator_id: "", assigned_dealer_id: "",
  });
  const [salesReps, setSalesReps] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploaded, setUploaded] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Existing-contact picker state
  const [existingList, setExistingList] = useState([]);
  const [pickedId, setPickedId] = useState("");

  // Load existing customers + dealers so Sales / Sales Coord don't retype known
  // contact info. Dealer users are pulled from /api/users.
  useEffect(() => {
    (async () => {
      const merged = [];
      try {
        const { data: customers } = await api.get("/customers");
        for (const c of customers) merged.push({
          key: `cust:${c.id}`, kind: "customer", name: c.name,
          phone: c.phone || "", email: c.email || "", address: c.location || "",
        });
      } catch (_) { /* ignore */ }
      try {
        const { data: users } = await api.get("/users");
        for (const u of (users || []).filter((u) => u.role === "dealer")) merged.push({
          key: `dealer:${u.id}`, kind: "dealer", name: u.name,
          phone: u.phone || "", email: u.email || "", address: "",
        });
      } catch (_) { /* ignore */ }
      // De-dup by name+phone
      const seen = new Set();
      const dedup = [];
      for (const m of merged) {
        const k = `${m.name}|${m.phone}`.toLowerCase();
        if (seen.has(k)) continue;
        seen.add(k); dedup.push(m);
      }
      setExistingList(dedup);
    })();
  }, []);

  // Load sales reps + coordinators for pre-assignment dropdowns.
  // Sales users don't need the Sales Person field (they own their leads),
  // but every internal creator can pre-select a Sales Coordinator.
  const role = user?.role;
  const showSalesField = ["super_admin", "production_coordination"].includes(role);
  const showCoordField = ["super_admin", "sales", "sales_coordination", "production_coordination"].includes(role);
  // Sales / Sales Coord / Super Admin can hand off an enquiry directly to a
  // named dealer (dealer will see it in their /enquiries list).
  const showDealerField = ["super_admin", "sales", "sales_coordination"].includes(role);
  useEffect(() => {
    if (!showSalesField && !showCoordField && !showDealerField) return;
    (async () => {
      try {
        if (showSalesField) {
          const { data } = await api.get("/enquiry/sales");
          setSalesReps(data || []);
        }
        if (showCoordField) {
          const { data } = await api.get("/enquiry/coordinators");
          setCoordinators(data || []);
        }
        if (showDealerField) {
          const { data } = await api.get("/enquiry/dealers");
          setDealers(data || []);
        }
      } catch (_) { /* silent */ }
    })();
  }, [showSalesField, showCoordField, showDealerField]);

  const applyPicked = (key) => {
    setPickedId(key);
    if (!key) return; // "New contact" — leave state as-is
    const found = existingList.find((x) => x.key === key);
    if (!found) return;
    setState((s) => ({
      ...s,
      name: found.name,
      phone: found.phone,
      email: found.email,
      address: found.address,
    }));
  };

  const toggleReq = (r) => setState((s) => ({
    ...s,
    requirements: s.requirements.includes(r) ? s.requirements.filter((x) => x !== r) : [...s.requirements, r],
  }));

  const addFiles = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setUploading(true);
    try {
      const newIds = [];
      for (const f of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", f);
        // Reuse the public upload — same object storage, still requires nothing extra
        const { data } = await api.post("/enquiry/public/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newIds.push({ id: data.id, name: data.name, size: data.size });
      }
      setFiles((prev) => [...prev, ...Array.from(fileList)]);
      setUploaded((prev) => [...prev, ...newIds]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setUploaded((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!state.name.trim() || !state.phone.trim()) return toast.error("Name and phone are required");
    setBusy(true);
    try {
      const { data } = await api.post("/enquiry", {
        ...state,
        email: state.email || undefined,
        assigned_sales_id: state.assigned_sales_id || undefined,
        assigned_coordinator_id: state.assigned_coordinator_id || undefined,
        assigned_dealer_id: state.assigned_dealer_id || undefined,
        file_ids: uploaded.map((f) => f.id),
      });
      toast.success(`Enquiry ${data.enquiry_id} created`);
      navigate(`/enquiries/${data.id}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <DesktopLayout>
      <Link to="/enquiries" className="hk-tiny-label text-orange-600 hover:text-orange-700 inline-flex items-center gap-1">
        <ChevronLeft className="w-3 h-3" /> All enquiries
      </Link>
      <h1 className="text-4xl font-black tracking-tighter mt-2 mb-6" style={{ fontFamily: "Chivo" }}>New enquiry</h1>

      <form onSubmit={submit} className="max-w-2xl space-y-5 hk-card p-6">
        <FieldRow label="Pre-fill from existing contact (optional)">
          <select className={FIELD} value={pickedId} onChange={(e) => applyPicked(e.target.value)}
            data-testid="new-enquiry-existing-picker">
            <option value="">— Type a new contact —</option>
            {existingList.length > 0 && <optgroup label={`Customers (${existingList.filter(x => x.kind === "customer").length})`}>
              {existingList.filter((x) => x.kind === "customer").map((c) => (
                <option key={c.key} value={c.key}>{c.name}{c.phone ? ` · ${c.phone}` : ""}</option>
              ))}
            </optgroup>}
            {existingList.some((x) => x.kind === "dealer") && <optgroup label="Dealers">
              {existingList.filter((x) => x.kind === "dealer").map((c) => (
                <option key={c.key} value={c.key}>{c.name}{c.phone ? ` · ${c.phone}` : ""}</option>
              ))}
            </optgroup>}
          </select>
          <p className="text-[10px] text-slate-500 italic mt-1">
            Auto-fills the contact details below. You can still edit them before saving.
          </p>
        </FieldRow>
        <FieldRow label="Name" required>
          <input type="text" required data-testid="new-enquiry-name" className={FIELD}
            value={state.name} onChange={(e) => setState({ ...state, name: e.target.value })} />
        </FieldRow>
        <div className="grid sm:grid-cols-2 gap-4">
          <FieldRow label="Phone" required>
            <input type="tel" required data-testid="new-enquiry-phone" className={FIELD}
              value={state.phone} onChange={(e) => setState({ ...state, phone: e.target.value })} />
          </FieldRow>
          <FieldRow label="Email">
            <input type="email" data-testid="new-enquiry-email" className={FIELD}
              value={state.email} onChange={(e) => setState({ ...state, email: e.target.value })} />
          </FieldRow>
        </div>
        <FieldRow label="Site address">
          <textarea rows={2} className={FIELD} data-testid="new-enquiry-address"
            value={state.address} onChange={(e) => setState({ ...state, address: e.target.value })} />
        </FieldRow>
        <FieldRow label="Requirements">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {REQUIREMENTS.map((r) => (
              <label key={r} className={`flex items-center gap-2 border rounded-sm px-3 py-2 cursor-pointer text-sm ${state.requirements.includes(r) ? "bg-orange-50 border-orange-500 text-orange-900" : "border-slate-300 text-slate-700"}`}>
                <input type="checkbox" className="w-4 h-4 accent-orange-500"
                  checked={state.requirements.includes(r)} onChange={() => toggleReq(r)} />
                {r}
              </label>
            ))}
          </div>
        </FieldRow>
        <FieldRow label="Timeline">
          <select className={FIELD} data-testid="new-enquiry-timeline"
            value={state.timeline} onChange={(e) => setState({ ...state, timeline: e.target.value })}>
            <option value="">— Select —</option>
            {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </FieldRow>
        <FieldRow label="Upload files (optional)">
          <label htmlFor="new-enq-files" className="flex items-center gap-3 border border-dashed border-slate-300 rounded-sm p-3 cursor-pointer hover:border-orange-400 hover:bg-orange-50/40">
            {uploading ? <Loader2 className="w-5 h-5 text-orange-500 animate-spin" /> : <Upload className="w-5 h-5 text-slate-500" />}
            <div className="text-sm text-slate-700">{uploading ? "Uploading…" : "Add drawings, photos, PDFs"}</div>
            <input id="new-enq-files" type="file" multiple hidden accept="image/*,application/pdf,.dwg,.dxf"
              onChange={(e) => addFiles(e.target.files)} />
          </label>
          {files.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {files.map((f, idx) => (
                <li key={idx} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-sm">
                  <span className="flex items-center gap-2 min-w-0"><Paperclip className="w-3.5 h-3.5 text-slate-400 shrink-0" /><span className="truncate">{f.name}</span></span>
                  <button type="button" onClick={() => removeFile(idx)} className="text-slate-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>
          )}
        </FieldRow>
        <FieldRow label="Notes">
          <textarea rows={3} className={FIELD} data-testid="new-enquiry-notes"
            value={state.notes} onChange={(e) => setState({ ...state, notes: e.target.value })} />
        </FieldRow>
        {(showSalesField || showCoordField || showDealerField) && (
          <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-slate-200">
            {showSalesField && (
              <FieldRow label="Assign Sales Person (optional)">
                <select className={FIELD} value={state.assigned_sales_id}
                  onChange={(e) => setState({ ...state, assigned_sales_id: e.target.value })}
                  data-testid="new-enquiry-sales">
                  <option value="">— Not assigned —</option>
                  {salesReps.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.email})</option>)}
                </select>
              </FieldRow>
            )}
            {showCoordField && (
              <FieldRow label="Assign Sales Coordinator (optional)">
                <select className={FIELD} value={state.assigned_coordinator_id}
                  onChange={(e) => setState({ ...state, assigned_coordinator_id: e.target.value })}
                  data-testid="new-enquiry-coord">
                  <option value="">— Not assigned —</option>
                  {coordinators.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
                </select>
                <p className="text-[10px] text-slate-500 italic mt-1">
                  Setting this jumps the enquiry straight to <b>attended</b> — Push to 2D/3D Design becomes available immediately.
                </p>
              </FieldRow>
            )}
            {showDealerField && (
              <FieldRow label="Assign Dealer (optional)">
                <select className={FIELD} value={state.assigned_dealer_id}
                  onChange={(e) => setState({ ...state, assigned_dealer_id: e.target.value })}
                  data-testid="new-enquiry-dealer">
                  <option value="">— Not assigned —</option>
                  {dealers.map((d) => <option key={d.id} value={d.id}>{d.name} ({d.email})</option>)}
                </select>
                <p className="text-[10px] text-slate-500 italic mt-1">
                  The chosen dealer will see this enquiry (and any quote generated from it) in their own list.
                </p>
              </FieldRow>
            )}
          </div>
        )}
        <div className="flex items-center gap-3 pt-2">
          <button type="submit" disabled={busy} data-testid="new-enquiry-submit"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-5 py-2.5 rounded-sm disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save enquiry"}
          </button>
          <Link to="/enquiries" className="text-sm text-slate-600 hover:text-slate-900">Cancel</Link>
        </div>
      </form>
    </DesktopLayout>
  );
}

function FieldRow({ label, required, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-800 mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}
